/**
 * AdminDecisionModal Component
 *
 * Comprehensive modal for admin decision workflow on beta applications.
 * Provides approve/reject/waitlist options with admin notes and email notification controls.
 */

import React, { useState } from 'react';
import { CheckCircle, XCircle, Clock, Send, UserCheck } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  RadioGroup,
  Radio,
  TextArea,
  Checkbox,
  CheckboxGroup
} from '@enfomentis/ui';
import type { BetaApplication, BetaApplicationStatus } from '@/types/supabase/BetaApplication';

/**
 * Admin decision data structure
 */
export interface AdminDecisionData {
  /** Selected application status */
  status: BetaApplicationStatus;
  /** Admin notes for the decision */
  notes: string;
  /** Whether to send email notification */
  sendEmail: boolean;
  /** Whether to send invitation email (only for approved applications) */
  sendInvitationEmail: boolean;
}

/**
 * Props for AdminDecisionModal component
 */
export interface AdminDecisionModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Function to close modal */
  onClose: () => void;
  /** Beta application being reviewed */
  application: BetaApplication;
  /** Callback when decision is submitted */
  onSubmit: (decision: AdminDecisionData) => Promise<void>;
  /** Whether submission is in progress */
  isSubmitting?: boolean;
}

/**
 * Status option configuration with icons and styling
 */
const STATUS_OPTIONS = [
  {
    value: 'approved' as const,
    label: 'Approve',
    description: 'Accept application and grant beta access',
    icon: CheckCircle,
    color: 'success' as const
  },
  {
    value: 'rejected' as const,
    label: 'Reject',
    description: 'Decline application',
    icon: XCircle,
    color: 'danger' as const
  },
  {
    value: 'waitlisted' as const,
    label: 'Waitlist',
    description: 'Place on waiting list for future consideration',
    icon: Clock,
    color: 'warning' as const
  }
] as const;

/**
 * Admin Decision Modal Component
 *
 * Features:
 * - Radio button group for status selection (approve/reject/waitlist)
 * - Admin notes textarea for decision reasoning
 * - Email notification checkbox option
 * - Form validation with clear error states
 * - Mobile-responsive design with proper touch targets
 * - Accessibility compliance with ARIA labels
 * - Loading states during submission
 *
 * Usage:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * const handleDecision = async (decision: AdminDecisionData) => {
 *   await betaApplicationService.updateStatus(applicationId, decision);
 *   setIsOpen(false);
 * };
 *
 * <AdminDecisionModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   application={application}
 *   onSubmit={handleDecision}
 *   isSubmitting={submitting}
 * />
 * ```
 */
export const AdminDecisionModal: React.FC<AdminDecisionModalProps> = ({
  isOpen,
  onClose,
  application,
  onSubmit,
  isSubmitting = false
}) => {
  // Form state
  const [selectedStatus, setSelectedStatus] = useState<BetaApplicationStatus>('approved');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [emailOptions, setEmailOptions] = useState<string[]>(['sendEmail', 'sendInvitationEmail']);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedStatus('approved');
      setAdminNotes('');
      setEmailOptions(['sendEmail', 'sendInvitationEmail']); // Default to both email options
      setValidationErrors([]);
    }
  }, [isOpen]);

  // Form validation
  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!selectedStatus) {
      errors.push('Please select a decision status');
    }

    if (adminNotes.trim().length > 0 && adminNotes.trim().length < 10) {
      errors.push('Admin notes must be at least 10 characters if provided');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    const decisionData: AdminDecisionData = {
      status: selectedStatus,
      notes: adminNotes.trim(),
      sendEmail: emailOptions.includes('sendEmail'),
      sendInvitationEmail: selectedStatus === 'approved' ? emailOptions.includes('sendInvitationEmail') : false
    };

    try {
      await onSubmit(decisionData);
    } catch (error) {
      console.error('Failed to submit admin decision:', error);
      setValidationErrors(['Failed to submit decision. Please try again.']);
    }
  };

  // Handle modal close
  const handleClose = (): void => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-text-primary">
            Review Application
          </h3>
          <p className="text-sm text-text-secondary">
            {application.fullName} ({application.email})
          </p>
        </ModalHeader>

        <ModalBody className="space-y-6">
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="p-3 bg-danger-50 dark:bg-danger-950/20 border border-danger-200 dark:border-danger-800 rounded-md">
              <div className="space-y-1">
                {validationErrors.map((error, index) => (
                  <p key={index} className="text-sm text-danger-600 dark:text-danger-400">
                    {error}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Status Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-text-primary">
              Decision <span className="text-danger-500">*</span>
            </label>
            <RadioGroup
              value={selectedStatus}
              onValueChange={(value) => setSelectedStatus(value as BetaApplicationStatus)}
              orientation="vertical"
              classNames={{
                wrapper: "gap-3"
              }}
            >
              {STATUS_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <Radio
                    key={option.value}
                    value={option.value}
                    classNames={{
                      base: "inline-flex m-0 bg-background hover:bg-default-100 items-center justify-between flex-row-reverse max-w-full cursor-pointer rounded-lg gap-4 p-4 border border-default-200 data-[selected=true]:border-primary",
                      wrapper: "border-0"
                    }}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Icon
                        size={20}
                        className={`text-${option.color}-500 flex-shrink-0`}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-text-primary">
                          {option.label}
                        </p>
                        <p className="text-xs text-text-secondary">
                          {option.description}
                        </p>
                      </div>
                    </div>
                  </Radio>
                );
              })}
            </RadioGroup>
          </div>

          {/* Admin Notes */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-text-primary">
              Admin Notes
            </label>
            <TextArea
              placeholder="Optional notes about this decision..."
              value={adminNotes}
              onValueChange={setAdminNotes}
              minRows={3}
              maxRows={6}
              classNames={{
                input: "resize-none text-base"
              }}
              description="Provide context for your decision (optional but recommended)"
            />
          </div>

          {/* Email Notification Options */}
          <div className="space-y-3">
            <CheckboxGroup
              label="Email Options"
              value={emailOptions}
              onValueChange={setEmailOptions}
              orientation="vertical"
              classNames={{
                wrapper: "space-y-3"
              }}
            >
              <Checkbox
                value="sendEmail"
                classNames={{
                  base: "max-w-none w-full m-0 p-3 border border-default-200 rounded-lg hover:border-default-300 hover:shadow-sm transition-all duration-200 data-[selected=true]:border-primary data-[selected=true]:bg-primary-50",
                  wrapper: "mr-3"
                }}
              >
                <div className="flex items-center gap-3 w-full">
                  <Send size={18} className="text-primary-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-primary">
                      Send notification email
                    </p>
                    <p className="text-xs text-text-secondary">
                      Notify applicant of your decision via email
                    </p>
                  </div>
                </div>
              </Checkbox>

              {/* Invitation Email Option - Only shown for approved applications */}
              {selectedStatus === 'approved' && (
                <Checkbox
                  value="sendInvitationEmail"
                  classNames={{
                    base: "max-w-none w-full m-0 p-3 border border-default-200 rounded-lg hover:border-default-300 hover:shadow-sm transition-all duration-200 data-[selected=true]:border-primary data-[selected=true]:bg-primary-50",
                    wrapper: "mr-3"
                  }}
                >
                  <div className="flex items-center gap-3 w-full">
                    <UserCheck size={18} className="text-success-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary">
                        Send activation email
                      </p>
                      <p className="text-xs text-text-secondary">
                        Send activation link so user can set up their account right away
                      </p>
                    </div>
                  </div>
                </Checkbox>
              )}
            </CheckboxGroup>
          </div>
        </ModalBody>

        <ModalFooter className="gap-3">
          <Button
            variant="flat"
            onPress={handleClose}
            isDisabled={isSubmitting}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
            startContent={<CheckCircle size={16} />}
            className="min-w-[140px]"
          >
            Submit Decision
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};